// What a file's name says it is — Live's groups, read off the words
// producers actually use: the vocabulary's aliases, so the name reader, the
// `#` search and the completion share one list. The coarse family words
// (shared/library-tags.ts tagsForName) stay for `#` search; these are the
// finer, storable tags.
//
// Pure data, in shared/: the browser reads a dropped file's name with it and
// the Worker reads a kept clip's, so both suggest the same tags.

import type { NewTag } from "../api-types.js";
import { KEY_ROOTS, TAG_GROUPS, keyTag, normalizeTag, parseTag, tagsForName, validTag } from "../library-tags.js";

/**
 * The rule table, built from the vocabulary's aliases (shared/library-tags.ts):
 * for the groups a name can say — Drums, Sounds, Genre — a value matches when
 * its id, its label or one of its aliases appears as a whole word or phrase.
 * Character is left out: "dry" or "wet" in a name is as often a fact about
 * the file as about the sound. The key reader below handles `key:`.
 */
const NAMED_GROUPS: ReadonlySet<string> = new Set(["drums", "sounds", "genre"]);

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

const RULES: ReadonlyArray<[RegExp, string]> = TAG_GROUPS.filter((g) => NAMED_GROUPS.has(g.id) && g.values !== null).flatMap((g) =>
  g.values!.map((v): [RegExp, string] => {
    const phrases = new Set<string>([v.id.replace(/-/g, " "), v.label.toLowerCase().replace(/[^a-z0-9& ]+/g, " ").trim(), ...(v.aliases ?? [])]);
    const alts = [...phrases].filter((x) => x !== "").map(escapeRe).join("|");
    return [new RegExp(`\\b(${alts})\\b`), `${g.id}:${v.id}`];
  }),
);

/** A genre tag never overrides a sound; "ambient pad" is a pad in the ambient genre, both. */

const NOTE_PC: Record<string, number> = { c: 0, d: 2, e: 4, f: 5, g: 7, a: 9, b: 11 };

/**
 * A key named in the file name — only with a mode marker, so "Sub stab C"
 * is not C major: `F#m`, `Am`, `A minor`, `Db maj`, `Bb major`. Flats fold
 * to the sharp slug.
 */
export function keyFromName(name: string): string | null {
  const m = /(?:^|[^a-z])([a-g])\s?([#♯b♭])?\s?(m|min|minor|maj|major)(?![a-z])/i.exec(name);
  if (!m) return null;
  const letter = m[1]!.toLowerCase();
  const accidental = m[2];
  const mode = /^maj/i.test(m[3]!) ? "major" : "minor";
  // "Bm" is a key; "Bb" alone is a note but no mode; "b minor" fine. A lone
  // trailing "b" followed by "m" reads as flat only when there is a letter
  // before it ("Dbm"); handled by the regex order above.
  let pc = NOTE_PC[letter]!;
  if (accidental === "#" || accidental === "♯") pc = (pc + 1) % 12;
  if (accidental === "b" || accidental === "♭") pc = (pc + 11) % 12;
  if (KEY_ROOTS[pc] === undefined) return null;
  return keyTag(pc, mode);
}

/** The group tags a string of words earns, in rule order; a key when one is named. */
function tagsIn(text: string): string[] {
  const words = text
    .replace(/\.[a-z0-9]{2,4}$/i, "")
    .replace(/[_\-.()[\]]+/g, " ")
    .toLowerCase();
  const out: string[] = [];
  for (const [re, tag] of RULES) if (re.test(words) && !out.includes(tag)) out.push(tag);
  const key = keyFromName(text);
  if (key) out.push(key);
  return out;
}

/** The tags a name earns, source `name`, in rule order; a key when one is named. */
export function suggestFromName(name: string): NewTag[] {
  return tagsIn(name).map((tag) => ({ tag, source: "name" }));
}

/**
 * Folder words that say nothing about a sound — the pack's furniture.
 * Lower-case, compared whole.
 */
export const FOLDER_STOP_WORDS: ReadonlySet<string> = new Set([
  "samples", "sample", "audio", "wav", "wavs", "aiff", "mp3", "flac", "loops", "loop", "one shots", "one-shots", "oneshots", "oneshot", "one shot",
  "hits", "stems", "stem", "pack", "packs", "kit", "kits", "vol", "volume", "misc", "other", "new", "old", "untitled", "folder", "files", "export",
  "exports", "bounce", "bounces", "render", "renders", "project", "projects", "sounds", "sound", "library", "clips", "clip", "music", "download", "downloads",
]);

/** How long a free word from a folder may be: a folder named with a sentence is not a tag. */
const FOLDER_WORD_MAX = 24;

/**
 * What one folder of a path says, source `folder`: the vocabulary's groups
 * (`Hats` → drums:hihat, `Vocals` → sounds:voice, `House` → genre:house, `Am`
 * → key:a-minor) — and when the vocabulary knows none of its words, the
 * folder's own name as a free word (`Night Bus` → #night-bus), unless it is
 * furniture (`Samples`, `Vol 2`), a number, or too long to be a tag.
 */
export function suggestFromFolder(segment: string): NewTag[] {
  const name = segment.trim();
  if (name === "") return [];
  const known = tagsIn(name);
  if (known.length > 0) return known.map((tag) => ({ tag, source: "folder" }));
  // "Drums", "Keys", "FX": a family word the search already derives from the
  // name — not a tag to store, and nothing finer to say.
  if (tagsForName(name).length > 0) return [];
  const lower = name.toLowerCase().replace(/[_\-.]+/g, " ").replace(/\s+/g, " ").trim();
  if (lower === "" || FOLDER_STOP_WORDS.has(lower) || /^[\d\s.-]+$/.test(lower) || lower.length > FOLDER_WORD_MAX) return [];
  // "Vol 2", "Pack 3": furniture with a number after it.
  if (/^[a-z]+ \d+$/.test(lower) && FOLDER_STOP_WORDS.has(lower.split(" ")[0]!)) return [];
  const word = normalizeTag(lower);
  return validTag(word) && parseTag(word).group === null ? [{ tag: word, source: "folder" }] : [];
}

/** The folders of a path, nearest first, with the shared root left out when asked. */
export function folderSegments(path: string, opts: { dropRoot?: boolean } = {}): string[] {
  const parts = path.split("/").slice(0, -1).filter((p) => p.trim() !== "");
  if (opts.dropRoot && parts.length > 0) parts.shift();
  return parts.reverse();
}
